import _defineProperty from "/app/node_modules/.pnpm/next@12.3.3_@babel+core@7.14.3_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Divider, DeprecatedIcon, InputBar } from '@vinted/web-ui';
import { isEmpty, trim } from 'lodash';
import uuid from 'uuid';
import { SEARCH_SUGGESTION_MIN_QUERY_LENGTH } from 'constants/index';
import { CATALOG_URL, FORUM_SEARCH_URL, HELP_SEARCH_URL, USER_SEARCH_URL } from 'constants/routes';
import { SearchBarSearchType, SearchSuggestionType } from 'constants/search';
import { HC_SEARCH_SESSION_ID_COOKIE_KEY } from 'constants/help-center';
import { MS_PER_SECOND, SECONDS_PER_DAY } from 'constants/date';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { SavedSearchType, SearchStartType } from 'constants/tracking/search';
import SearchSuggestions from 'components/SearchSuggestions';
import OutsideClick from 'components/OutsideClick';
import ListNavigator from 'components/ListNavigator';
import { SavedSearchesList, SearchSubscribeModal } from 'components/SavedSearches';
import useLocation from 'hooks/useLocation';
import useTranslate from 'hooks/useTranslate';
import useTracking from 'hooks/useTracking';
import { getIsSubscribedModalOpen, getSearches } from 'state/saved-searches/selectors';
import { searchDtoToUrlParams } from 'state/saved-searches/transformers';
import { checkCurrentSearch, fetchSearchesRequest, toggleSearchSubscription } from 'state/saved-searches/actions';
import { actions } from 'state/saved-searches/slice';
import { getSuggestions, getSuggestionsListId } from 'state/search-suggestions/selectors';
import { actions as suggestionsActions } from 'state/search-suggestions/slice';
import { getGlobalSearchSessionId, getSearchSessionId, getSelectedCatalogs } from 'state/catalog-filters/selectors';
import { actions as filterActions } from 'state/catalog-filters/slice';
import { searchSuggestionToFilters, searchSuggestionToUrlParams } from 'state/catalog-filters/utils';
import { getCountryCodeByIp, getUserCountryCode, getUserId } from 'state/session/selectors';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { clickEvent, selectSavedSearchEvent, selectSearchSuggestionEvent } from 'libs/common/event-tracker/events';
import { redirectToPage } from 'libs/utils/window';
import { normalizedQueryParam, toUrlQuery } from 'libs/utils/url';
import { setSearchStartData } from 'libs/utils/search';
import { saveCookie } from 'libs/utils/cookies';
import TypeSelector from './TypeSelector';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SEARCH_URLS = {
  [SearchBarSearchType.Item]: CATALOG_URL,
  [SearchBarSearchType.User]: USER_SEARCH_URL,
  [SearchBarSearchType.Faq]: HELP_SEARCH_URL,
  [SearchBarSearchType.Forum]: FORUM_SEARCH_URL
};

const isSavedSearchesRendered = query => isEmpty(query) || query.length < SEARCH_SUGGESTION_MIN_QUERY_LENGTH;

const SearchBar = _ref => {
  let {
    searchType: initialSearchType = SearchBarSearchType.Item,
    isInCatalog = false
  } = _ref;
  const {
    searchParams
  } = useLocation();
  const translate = useTranslate();
  const {
    track
  } = useTracking();
  const dispatch = useDispatch();
  const userId = useSelector(getUserId);
  const isSubscribeModalOpen = useSelector(getIsSubscribedModalOpen);
  const suggestions = useSelector(getSuggestions);
  const savedSearches = useSelector(getSearches);
  const selectedCatalogs = useSelector(getSelectedCatalogs);
  const userCountryCode = useSelector(getUserCountryCode);
  const countryCodeByIp = useSelector(getCountryCodeByIp);
  const searchSessionId = useSelector(getSearchSessionId);
  const globalSearchSessionId = useSelector(getGlobalSearchSessionId);
  const suggestionsListId = useSelector(getSuggestionsListId);
  const isUpdateSelectSearchSuggestionsEventEnabled = useSelector(getIsFeatureSwitchEnabled('web_update_select_search_suggestions_event'));
  const isUpdateFilterItemsEventEnabled = useSelector(getIsFeatureSwitchEnabled('web_update_filter_items_event'));
  const isSearchSuggestionsAutofillEnabled = useSelector(getIsFeatureSwitchEnabled('web_search_suggestions_autofill_when_moving_arrows'));
  const initialQuery = normalizedQueryParam(searchParams.search_text);
  const {
    0: query,
    1: setQuery
  } = useState(initialQuery);
  const {
    0: searchType,
    1: setSearchType
  } = useState(initialSearchType);
  const {
    0: isFocused,
    1: setIsFocused
  } = useState(false);
  const {
    0: isTypeSelectorOpen,
    1: setIsTypeSelectorOpen
  } = useState(false);
  const {
    0: selectedSearch,
    1: setSelectedSearch
  } = useState(null);
  const inputRef = useRef(null);
  const suggestionsSessionId = useRef(null);
  const isSavedSearchesFetched = useRef(false);
  useEffect(() => {
    if (!isInCatalog) return;
    dispatch(checkCurrentSearch());
  }, [dispatch, isInCatalog]);

  const setSuggestionsSessionId = id => {
    suggestionsSessionId.current = id;
  };

  const requestSearches = () => {
    if (!userId) return;
    dispatch(fetchSearchesRequest({
      userId
    }));
    isSavedSearchesFetched.current = true;
  };

  const querySearchSuggestions = queryInput => {
    if (searchType !== SearchBarSearchType.Item || isEmpty(queryInput)) return;

    if (!isSavedSearchesRendered(queryInput)) {
      const title = translate('search_suggestions.suggestion_fallback', {
        query: queryInput
      });
      dispatch(suggestionsActions.fetchSuggestionsRequest({
        query: queryInput
      }, {
        title
      }));
    }
  };

  const focusInput = () => {
    var _inputRef$current;

    return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
  };

  const blurInput = () => {
    var _inputRef$current2;

    return (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.blur();
  };

  const toggleTypeSelector = () => setIsTypeSelectorOpen(prevState => !prevState);

  const searchSuggestionUrl = suggestion => {
    const {
      item: itemSearchUrl
    } = SEARCH_URLS;
    if (isInCatalog) return undefined;

    if (suggestion.type === SearchSuggestionType.Scoped) {
      const filterQuery = toUrlQuery(searchSuggestionToUrlParams(suggestion));
      return "".concat(itemSearchUrl, "?").concat(filterQuery);
    }

    return "".concat(itemSearchUrl, "?search_text=").concat(encodeURIComponent(suggestion.query));
  };

  const getPlaceholder = () => {
    const isCatalogPlaceholder = isInCatalog && selectedCatalogs.length;

    if (searchType === SearchBarSearchType.Item && isCatalogPlaceholder) {
      const catalogName = selectedCatalogs.map(catalog => catalog.title).join(', ');
      return translate("searchbar.placeholder.".concat(searchType, "_with_catalog_selected"), {
        catalog: catalogName
      });
    }

    return translate("searchbar.placeholder.".concat(searchType));
  };

  const handleQueryChange = event => {
    const newQuery = event.currentTarget.value;
    if (!isEmpty(trim(newQuery))) querySearchSuggestions(newQuery);
    setQuery(newQuery);
  };

  const handleValueClear = () => setQuery('');

  const handleTypeSelectorClick = () => {
    setIsFocused(false);
    toggleTypeSelector();
    track(clickEvent({
      target: ClickableElement.SearchBarType
    }));
  };

  const handleTypeSelectorSelect = newSearchType => {
    toggleTypeSelector();
    focusInput();
    if (searchType !== newSearchType) setSearchType(newSearchType);
  };

  const handleOutClick = () => {
    // Prevent excessive re-rendering
    if (isSubscribeModalOpen || !(isTypeSelectorOpen || isFocused)) return;
    setIsTypeSelectorOpen(false);
    setIsFocused(false);
  };

  const handleInputFocus = () => {
    if (!isSavedSearchesFetched.current) requestSearches();

    if (!isSavedSearchesRendered(query) && !isFocused) {
      querySearchSuggestions(query);
    }

    setIsTypeSelectorOpen(false);
    setIsFocused(true);
  };

  const handleInputClick = () => track(clickEvent({
    target: ClickableElement.SearchBar
  }));

  const handleSubscribeClick = search => {
    if (!userId) return;
    dispatch(toggleSearchSubscription({
      userId,
      searchId: search.id
    }));
  };

  const handleSubscribeModalClose = () => {
    dispatch(actions.closeSubscribeModal());
  };

  const handleSuggestionClick = (suggestion, index, scopedSuggestionCount) => {
    const trackingArgs = {
      suggestions: suggestions.map(item => item.query),
      suggestion: suggestion.query,
      query,
      scopedSuggestionCount,
      index,
      countryCode: userCountryCode || countryCodeByIp,
      searchSessionId,
      globalSearchSessionId,
      suggestionsSessionId: suggestionsSessionId.current,
      isFrontendGeneratedSuggestion: suggestion.type === SearchSuggestionType.Fallback
    };

    if (suggestion.type === SearchSuggestionType.Scoped) {
      trackingArgs.subtitle = suggestion.subtitle;
    }

    if ('params' in suggestion) {
      trackingArgs.params = suggestion.params;
    }

    if (isUpdateSelectSearchSuggestionsEventEnabled) {
      trackingArgs.suggestionPosition = index + 1;
      trackingArgs.suggestionsListId = suggestionsListId;
    } else {
      trackingArgs.type = suggestion.type;

      if ('typeId' in suggestion) {
        trackingArgs.typeId = suggestion.typeId;
      }
    }

    track(selectSearchSuggestionEvent(trackingArgs));

    if (suggestionsSessionId.current) {
      setSearchStartData({
        searchStartId: suggestionsSessionId.current,
        searchStartType: SearchStartType.SearchSuggestions
      });
    }

    if (isInCatalog) {
      if (suggestion.type === SearchSuggestionType.Scoped) {
        dispatch(filterActions.setFilters({
          filters: searchSuggestionToFilters(suggestion)
        }));
      }

      dispatch(filterActions.setQuery({
        query: suggestion.query
      }));
    }

    setQuery(suggestion.query);
    setIsFocused(false);
  };

  const handleSavedSearchClick = savedSearch => {
    const savedSearchType = savedSearch.subscribed ? SavedSearchType.SubscribedSearch : SavedSearchType.RecentSearch;
    track(selectSavedSearchEvent({
      searchSessionId,
      type: savedSearchType,
      searchTitle: savedSearch.title,
      globalSearchSessionId,
      newItemsCount: savedSearch.new_items_count,
      unrestrictedNewItemsCount: savedSearch.unrestricted_new_items_count
    }));
  };

  const handleSubmit = event => {
    if (searchType === SearchBarSearchType.Faq) {
      const expirationDate = new Date();
      expirationDate.setTime(expirationDate.getTime() + SECONDS_PER_DAY * MS_PER_SECOND);
      saveCookie(HC_SEARCH_SESSION_ID_COOKIE_KEY, uuid.v4(), expirationDate);
    }

    if (searchType !== SearchBarSearchType.Item) return;
    if (isInCatalog) event.preventDefault();

    if (isUpdateFilterItemsEventEnabled && suggestionsSessionId.current) {
      setSearchStartData({
        searchStartId: suggestionsSessionId.current,
        searchStartType: SearchStartType.SearchManual
      });
    }

    blurInput();
    setIsFocused(false);
    dispatch(filterActions.setQuery({
      query
    }));
  };

  const handleSavedSearchOnEnter = highlightedResultIndex => {
    const highlightedSearch = savedSearches[highlightedResultIndex];

    const params = _objectSpread(_objectSpread({}, searchDtoToUrlParams(highlightedSearch)), {}, {
      search_id: highlightedSearch.id
    });

    const url = "".concat(SEARCH_URLS[SearchBarSearchType.Item], "?").concat(toUrlQuery(params));
    handleSavedSearchClick(highlightedSearch);
    redirectToPage(url);
    blurInput();
  };

  const handleSearchSuggestionOnEnter = highlightedResultIndex => {
    const highlightedSuggestion = suggestions[highlightedResultIndex];
    const url = searchSuggestionUrl(highlightedSuggestion);
    const scopedSuggestions = suggestions.filter(item => item.type === SearchSuggestionType.Scoped);
    handleSuggestionClick(highlightedSuggestion, highlightedResultIndex, scopedSuggestions.length);
    if (url) redirectToPage(url);
    blurInput();
  };

  const handleSuggestionNavigation = highlightedResultIndex => {
    if (highlightedResultIndex === null) {
      setSelectedSearch(null);
    } else if (isSavedSearchesRendered(query)) {
      setSelectedSearch(savedSearches[highlightedResultIndex].search_text);
    } else {
      setSelectedSearch(suggestions[highlightedResultIndex].query);
    }
  };

  const renderIcon = () => /*#__PURE__*/_jsx("div", {
    className: "u-flexbox u-align-items-center",
    children: /*#__PURE__*/_jsx(DeprecatedIcon, {
      name: "search-filled",
      size: DeprecatedIcon.Size.Small,
      color: DeprecatedIcon.Color.GreyscaleLevel4
    })
  });

  const renderPrefix = () => /*#__PURE__*/_jsxs("div", {
    className: "u-flexbox u-fill-height",
    children: [/*#__PURE__*/_jsx(TypeSelector, {
      selectedType: searchType,
      isOpen: isTypeSelectorOpen,
      onClick: handleTypeSelectorClick,
      onSelect: handleTypeSelectorSelect
    }), /*#__PURE__*/_jsx(Divider, {
      orientation: Divider.Orientation.Vertical
    })]
  });

  const renderSuggestions = highlightedResultIndex => /*#__PURE__*/_jsx(SearchSuggestions, {
    query: query,
    items: suggestions,
    highlightedIndex: highlightedResultIndex,
    onSuggestionClick: handleSuggestionClick,
    suggestionUrl: searchSuggestionUrl,
    searchSessionId: searchSessionId,
    globalSearchSessionId: globalSearchSessionId,
    setSuggestionsSessionId: setSuggestionsSessionId,
    suggestionsListId: suggestionsListId
  });

  const renderSavedSearches = highlightedResultIndex => {
    if (!userId || !savedSearches.length) return null;
    return /*#__PURE__*/_jsx(SavedSearchesList, {
      searches: savedSearches,
      highlightedIndex: highlightedResultIndex,
      onSubscribeClick: handleSubscribeClick,
      searchUrl: SEARCH_URLS[SearchBarSearchType.Item],
      searchSessionId: searchSessionId,
      globalSearchSessionId: globalSearchSessionId,
      onSearchClick: handleSavedSearchClick
    });
  };

  const renderDropdown = highlightedResultIndex => {
    if (searchType !== SearchBarSearchType.Item || !isFocused) return null;
    const dropdownContent = isSavedSearchesRendered(query) ? renderSavedSearches(highlightedResultIndex) : renderSuggestions(highlightedResultIndex);
    if (isEmpty(dropdownContent)) return null;
    return /*#__PURE__*/_jsx("div", {
      className: "u-ui-margin-top-regular u-position-absolute u-fill-width",
      children: /*#__PURE__*/_jsx(Card, {
        styling: Card.Styling.Lifted,
        children: /*#__PURE__*/_jsx("div", {
          className: "u-overflow-hidden",
          children: dropdownContent
        })
      })
    });
  };

  const renderAccessChannel = () => {
    if (searchType !== 'faq') return null;
    return /*#__PURE__*/_jsx("input", {
      type: "hidden",
      name: "access_channel",
      value: "hc_search"
    });
  };

  let resultsCount = savedSearches.length;
  let onSelect = handleSavedSearchOnEnter;

  if (!isSavedSearchesRendered(query)) {
    resultsCount = suggestions.length;
    onSelect = handleSearchSuggestionOnEnter;
  }

  return /*#__PURE__*/_jsx(OutsideClick, {
    onOutsideClick: handleOutClick,
    children: /*#__PURE__*/_jsxs("form", {
      onSubmit: handleSubmit,
      method: "get",
      action: SEARCH_URLS[searchType],
      children: [renderAccessChannel(), /*#__PURE__*/_jsx(ListNavigator, {
        itemCount: resultsCount,
        onSelect: onSelect,
        onHighlightedResultChange: isSearchSuggestionsAutofillEnabled ? handleSuggestionNavigation : undefined,
        children: _ref2 => {
          let {
            highlightedResultIndex,
            handleKeyNavigation
          } = _ref2;
          return /*#__PURE__*/_jsxs("div", {
            className: "u-position-relative",
            children: [/*#__PURE__*/_jsx(InputBar, {
              inputAria: {
                'aria-labelledby': "search-".concat(searchType)
              },
              clearButtonAria: {
                'aria-label': translate('searchbar.button.clear')
              },
              name: "search_text",
              placeholder: getPlaceholder(),
              value: isSearchSuggestionsAutofillEnabled ? selectedSearch || query : query,
              deprecatedIcon: renderIcon(),
              prefix: renderPrefix(),
              onChange: handleQueryChange,
              onFocus: handleInputFocus,
              onKeyDown: handleKeyNavigation,
              onValueClear: handleValueClear,
              onInputClick: handleInputClick,
              ref: inputRef,
              testId: "search-text"
            }), renderDropdown(highlightedResultIndex)]
          });
        }
      }), /*#__PURE__*/_jsx(SearchSubscribeModal, {
        isOpen: isSubscribeModalOpen,
        onClose: handleSubscribeModalClose
      })]
    })
  });
};

export default SearchBar;